import type { User } from '#auth-utils'
import {
  OIDC_DATA_KEY,
  TOKEN_DATA_KEY,
  userSessionState,
} from '@aschehoug/aschehoug-oidc-client'

export const useAuthorizeStore = defineStore('authorize', () => {
  const { time } = useServerTiming()

  const { hasConfirmedSelectedOrganization } = useOrganizationPicker()

  const user = ref<User>()
  const accessToken = useCookie('__accessToken')

  const loggedIn = computed(() => !!user.value)

  async function headers() {
    const client = useOidcClient()

    if (import.meta.client) {
      accessToken.value ||= client.getAccessToken()
    }

    if (!accessToken.value) {
      console.warn('No access token')
      return
    }

    if (isExpired(accessToken.value)) {
      try {
        const state = await time('refreshAccessToken', () =>
          client.refreshAccessToken())
        if (state === userSessionState.hasActiveSession) {
          accessToken.value = client.getAccessToken()
        } else {
          logout(true)
          navigateTo({ name: 'index' })
          return
        }
      } catch (e) {
        handleError(e, '', false)
        logout(true)
        navigateTo({ name: 'index' })
        return
      }
    }

    return {
      Authorization: `Bearer ${accessToken.value}`,
    }
  }

  const queryClient = useQueryClient()

  function logout(local = false) {
    hasConfirmedSelectedOrganization.value = false
    queryClient.removeQueries({
      queryKey: [user.value?.userId],
    })
    user.value = undefined
    useCookie('__accessToken').value = null
    accessToken.value = undefined
    localStorage.removeItem(TOKEN_DATA_KEY)
    localStorage.removeItem(OIDC_DATA_KEY)

    if (local) {
      return
    }

    return navigateTo('/logout', { external: true })
  }

  async function refreshUserData() {
    user.value = await time('refreshUserData', async () =>
      $fetch<User>('/api/user', {
        headers: await headers(),
      }))

    const orgCount = user.value?.schoolOrganizations?.length ?? 0
    if (orgCount <= 1) {
      hasConfirmedSelectedOrganization.value = true
    }
  }

  return {
    accessToken,
    loggedIn,
    user,
    logout,
    headers,
    refreshUserData,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthorizeStore, import.meta.hot))
}
